import { useId, type TextareaHTMLAttributes } from "react";
import { IconAlert } from "../icons/icons";
import styles from "./TextField.module.css";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
}

/** Multi-line sibling of `TextField`, sharing its box, label, hint, and
 * error styling so a form mixing both reads as one field family. */
export function Textarea({ label, hint, error, optional, id, className, ...rest }: TextareaProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className={[styles.field, className].filter(Boolean).join(" ")}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
        {optional && <span className={styles.optional}>optional</span>}
      </div>
      <textarea
        id={inputId}
        className={styles.input}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
        {...rest}
      />
      {hint && !error && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className={styles.error} role="alert">
          <IconAlert width={14} height={14} />
          {error}
        </span>
      )}
    </div>
  );
}
